import cn from "classnames";
import styles from "./Table.module.scss";
import { flexRender } from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TableFilter from "../TableFilter/TableFilter";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { ITable } from "./Table.props";
import { useEffect, useRef, useState } from "react";
import { useTableKeyboardNavigation } from "@/hooks";
import { BlockWithInfo, TableCell } from "@/ui";
import { Box } from "@mui/material";

const Table = <T,>({
	table,
	isRowHoverEnebled = false,
	onRowClick,
	selectedRowId,
	tableContainerRef,
	completeHeaderOptions,
	editableColumns = [], // по умолчанию нет редактируемых колонок
	rowSelection,
	changedCellList = [], // по умолчанию пусто
	setChangedQuestions,
	handleSubmit,
	autocompleteCellOptions = {}, // по умолчанию пусто
	children,
	withFilters = false,
	headerPadding = "0 0 0 6px",
	columnsWidth = (
		<colgroup>
			<col style={{ width: "4%", minWidth: "60px" }} />
			<col style={{ width: "14%" }} />
			<col style={{ width: "60%" }} />
			<col style={{ width: "12%" }} />
			<col style={{ width: "10%" }} />
		</colgroup>
	),
	...props
}: ITable<T>) => {
	const [focusedCell, setFocusedCell] = useState<{ row: number; column: number }>({ row: 0, column: 0 });
	const [hoveredEl, setHoveredEl] = useState("");
	const cellRefs = useRef<Record<string, HTMLTableCellElement | null>>({});
	const { rows } = table.getRowModel();

	const { keyDownHandler } = useTableKeyboardNavigation({
		table,
		focusedCell,
		setFocusedCell,
		handleSubmit: handleSubmit ?? (() => {}),
	});

	const normalizedChangedCellList = changedCellList.reduce((acc, el) => ({ ...acc, ...el }), {});

	useEffect(() => {
		const key = `${focusedCell.row}-${focusedCell.column}`;
		const el = cellRefs.current[key];
		if (el) {
			el.focus();
		}
	}, [focusedCell]);

	return (
		<Box sx={{ position: "relative", width: "100%", height: "100%" }}>
			<table {...props} className={cn(styles.table)}>
				{columnsWidth}
				<thead className={cn(styles.thead)}>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr key={headerGroup.id} className={cn(styles.hrow)}>
							{headerGroup.headers.map((header) => {
								return (
									<th
										key={header.id}
										className={cn(styles.th, { [styles.checkbox]: header.column.id === "select" })}
										{...{
											colSpan: header.colSpan,
											style: { width: header.getSize(), padding: headerPadding },
										}}
									>
										{!header.isPlaceholder && (
											<>
												<div onClick={header.column.getToggleSortingHandler()} className={header.id === "select" ? styles.title__select : styles.title__row}>
													{flexRender(header.column.columnDef.header, header.getContext())}
													{{ asc: <FontAwesomeIcon icon={faArrowUp} />, desc: <FontAwesomeIcon icon={faArrowDown} /> }[header.column.getIsSorted() as string] ?? null}
												</div>
												{header.column.getCanFilter() && withFilters && completeHeaderOptions && (
													<TableFilter<T> table={table} options={completeHeaderOptions[header.id]} column={header.column} rowSelection={rowSelection ?? {}} />
												)}
											</>
										)}
									</th>
								);
							})}
						</tr>
					))}
				</thead>
				<tbody style={{ position: "relative" }}>
					{rows.length !== 0 &&
						rows.map((row, i) => {
							const onMouseEnter = () => setHoveredEl(row.id);
							const onMouseLeave = () => setHoveredEl("");

							return (
								<tr
									// @ts-ignore — в данных точно есть id
									key={row.original.id}
									// @ts-ignore
									id={row.original.id}
									className={cn(styles.tr, {
										// @ts-ignore
										[styles["tr-hover"]]: isRowHoverEnebled && row.original.uuid !== selectedRowId,
										// @ts-ignore
										[styles["tr-selected"]]: (selectedRowId ? true : false) && (row.original.uuid === selectedRowId || row.original.id === selectedRowId),
									})}
									data-index={i}
									onClick={onRowClick ? () => onRowClick(row.original) : () => {}}
									onMouseEnter={onMouseEnter}
									onMouseLeave={onMouseLeave}
								>
									{row.getVisibleCells().map((cell, colIndex) => {
										const rowIndex = cell.row.index;
										const isFocused = rowIndex === focusedCell.row && colIndex === focusedCell.column;

										const handleFocus = () => {
											setFocusedCell({ row: rowIndex, column: colIndex });
											cellRefs.current[`${rowIndex}-${colIndex}`]?.focus();
										};

										const uniqueColumnAutocompleteOptions = Array.from(new Set(autocompleteCellOptions?.[cell.column.id as keyof T] ?? []));

										return editableColumns.includes(cell.column.id) ? (
											<TableCell<T>
												key={cell.id}
												cell={cell}
												isEditable={true}
												focusedCell={focusedCell}
												cellIndex={{ row: rowIndex, column: colIndex }}
												ref={(el: HTMLTableCellElement | null) => {
													cellRefs.current[`${rowIndex}-${colIndex}`] = el;
												}}
												changedCellList={normalizedChangedCellList}
												setChangedCellValue={setChangedQuestions ?? (() => {})}
												isFocused={isFocused}
												setFocusedCell={handleFocus}
												keyDownHandler={keyDownHandler}
												isHover={row.id === hoveredEl}
												autocompleteOptions={uniqueColumnAutocompleteOptions}
											>
												{cell.column.id === "select" ? children : null}
											</TableCell>
										) : (
											<TableCell<T> key={cell.id} cell={cell} cellIndex={{ row: rowIndex, column: colIndex }} isHover={row.id === hoveredEl}>
												{cell.column.id === "select" ? children : null}
											</TableCell>
										);
									})}
								</tr>
							);
						})}
				</tbody>
			</table>
			{rows.length === 0 && (
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "100%",
						pointerEvents: "none",
					}}
				>
					<BlockWithInfo title="title_emptyList" />
				</Box>
			)}
		</Box>
	);
};

export default Table;
